/**
 * リトライハンドラーユーティリティ
 * 非機能要件の可用性要件を満たすリトライ機能とフェイルオーバー機能
 */

import { performanceMonitor } from './performanceMonitor';
import { AppError, ErrorCategory, ErrorLevel, handleError } from './errorHandler';
import { TimeoutError } from './apiTimeout';
import logger from './logger';

// リトライ戦略タイプ
export enum RetryStrategy {
  FIXED = 'fixed', // 固定間隔
  EXPONENTIAL = 'exponential', // 指数バックオフ
  LINEAR = 'linear', // 線形増加
  FIBONACCI = 'fibonacci', // フィボナッチ数列
}

// リトライ設定
export interface RetryConfig {
  maxAttempts: number;
  strategy: RetryStrategy;
  baseDelayMs: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  jitter?: boolean; // ランダムなゆらぎを追加
  retryCondition?: (error: Error, attempt: number) => boolean;
  onRetry?: (error: Error, attempt: number, nextDelayMs: number) => void;
  onMaxAttemptsReached?: (error: Error) => void;
}

// リトライ実行結果
export interface RetryResult<T> {
  success: boolean;
  data?: T;
  error?: Error;
  attempts: number;
  totalDurationMs: number;
}

// 標準的なリトライ設定
export const RetryConfigs = {
  // API呼び出し用（短期間、少ない試行回数）
  API_QUICK: {
    maxAttempts: 3,
    strategy: RetryStrategy.EXPONENTIAL,
    baseDelayMs: 1000,
    maxDelayMs: 5000,
    backoffMultiplier: 2,
    jitter: true,
  },

  // ネットワーク呼び出し用（中期間、中程度の試行回数）
  NETWORK: {
    maxAttempts: 5,
    strategy: RetryStrategy.EXPONENTIAL,
    baseDelayMs: 2000,
    maxDelayMs: 30000,
    backoffMultiplier: 1.5,
    jitter: true,
  },

  // 配車計算用（長期間、多い試行回数）
  DISPATCH_CALCULATION: {
    maxAttempts: 3,
    strategy: RetryStrategy.FIXED,
    baseDelayMs: 5000,
    maxDelayMs: 10000,
    jitter: false,
  },

  // 重要な処理用（最大努力）
  CRITICAL: {
    maxAttempts: 10,
    strategy: RetryStrategy.FIBONACCI,
    baseDelayMs: 1000,
    maxDelayMs: 60000,
    jitter: true,
  },

  // 軽微な処理用（高速失敗）
  FAST_FAIL: {
    maxAttempts: 2,
    strategy: RetryStrategy.FIXED,
    baseDelayMs: 500,
    maxDelayMs: 1000,
    jitter: false,
  },
} as const;

/**
 * リトライ可能なエラーかどうかを判定
 */
export function isRetryableError(error: Error): boolean {
  // AppErrorの場合、retryableフラグを確認
  if (error instanceof AppError) {
    return error.retryable;
  }

  // TimeoutErrorは基本的にリトライ可能
  if (error instanceof TimeoutError) {
    return true;
  }

  // ネットワーク関連エラーはリトライ可能
  const retryablePatterns = [
    /network/i,
    /timeout/i,
    /fetch/i,
    /connection/i,
    /temporary/i,
    /service.*unavailable/i,
    /server.*error/i,
    /502|503|504/,
    /rate.*limit/i,
  ];

  const errorMessage = error.message.toLowerCase();
  const errorName = error.name.toLowerCase();

  return retryablePatterns.some(pattern => pattern.test(errorMessage) || pattern.test(errorName));
}

/**
 * リトライ間隔を計算
 */
function calculateDelay(
  attempt: number,
  strategy: RetryStrategy,
  baseDelayMs: number,
  maxDelayMs: number = Number.MAX_SAFE_INTEGER,
  backoffMultiplier: number = 2,
  jitter: boolean = false
): number {
  let delay: number;

  switch (strategy) {
    case RetryStrategy.FIXED:
      delay = baseDelayMs;
      break;

    case RetryStrategy.LINEAR:
      delay = baseDelayMs * attempt;
      break;

    case RetryStrategy.EXPONENTIAL:
      delay = baseDelayMs * Math.pow(backoffMultiplier, attempt - 1);
      break;

    case RetryStrategy.FIBONACCI:
      delay = baseDelayMs * fibonacci(attempt);
      break;

    default:
      delay = baseDelayMs;
  }

  // 最大遅延時間の制限
  delay = Math.min(delay, maxDelayMs);

  // ジッターの追加（雷群問題を防ぐ）
  if (jitter) {
    const jitterAmount = delay * 0.1; // 10%のゆらぎ
    delay += (Math.random() - 0.5) * 2 * jitterAmount;
  }

  return Math.max(0, Math.floor(delay));
}

/**
 * フィボナッチ数列計算
 */
function fibonacci(n: number): number {
  if (n <= 1) return 1;
  if (n === 2) return 1;

  let a = 1,
    b = 1;
  for (let i = 3; i <= n; i++) {
    [a, b] = [b, a + b];
  }
  return b;
}

/**
 * 非同期処理のリトライ実行
 */
export async function retryAsync<T>(
  operation: () => Promise<T>,
  config: RetryConfig,
  operationName: string = 'retry-operation'
): Promise<RetryResult<T>> {
  const startTime = performance.now();
  let lastError: Error = new Error('Unknown error');
  let attempts = 0;

  // パフォーマンス監視開始
  performanceMonitor.startMeasure(operationName, {
    type: 'retry-operation',
    maxAttempts: config.maxAttempts,
    strategy: config.strategy,
  });

  try {
    for (attempts = 1; attempts <= config.maxAttempts; attempts++) {
      try {
        const result = await operation();

        // 成功した場合
        const totalDurationMs = performance.now() - startTime;
        performanceMonitor.endMeasure(operationName);

        if (attempts > 1) {
          logger.info(
            `✅ Retry successful: "${operationName}" after ${attempts} attempts (${totalDurationMs.toFixed(2)}ms)`
          );
        }

        return {
          success: true,
          data: result,
          attempts,
          totalDurationMs,
        };
      } catch (error) {
        lastError = error as Error;

        // リトライ条件をチェック
        const shouldRetry = config.retryCondition
          ? config.retryCondition(lastError, attempts)
          : isRetryableError(lastError);

        // 最大試行回数に達した場合、またはリトライ不可能な場合
        if (attempts >= config.maxAttempts || !shouldRetry) {
          break;
        }

        // 次のリトライまでの遅延時間を計算
        const nextDelayMs = calculateDelay(
          attempts,
          config.strategy,
          config.baseDelayMs,
          config.maxDelayMs,
          config.backoffMultiplier,
          config.jitter
        );

        logger.warn(
          `🔄 Retry ${attempts}/${config.maxAttempts}: "${operationName}" failed, retrying in ${nextDelayMs}ms`,
          { error: lastError.message, attempt: attempts }
        );

        // onRetryコールバック実行
        if (config.onRetry) {
          config.onRetry(lastError, attempts, nextDelayMs);
        }

        // 遅延実行
        if (nextDelayMs > 0) {
          await new Promise(resolve => setTimeout(resolve, nextDelayMs));
        }
      }
    }

    // すべての試行が失敗した場合
    const totalDurationMs = performance.now() - startTime;
    performanceMonitor.endMeasure(operationName);

    logger.error(
      `❌ All retries failed: "${operationName}" after ${attempts} attempts (${totalDurationMs.toFixed(2)}ms)`
    );

    // onMaxAttemptsReachedコールバック実行
    if (config.onMaxAttemptsReached) {
      config.onMaxAttemptsReached(lastError);
    }

    // エラー処理
    handleError(lastError);

    return {
      success: false,
      error: lastError,
      attempts,
      totalDurationMs,
    };
  } catch (error) {
    // 予期しないエラー
    const totalDurationMs = performance.now() - startTime;
    performanceMonitor.endMeasure(operationName);

    return {
      success: false,
      error: error as Error,
      attempts,
      totalDurationMs,
    };
  }
}

/**
 * 同期処理のリトライ実行
 */
export function retrySync<T>(
  operation: () => T,
  config: Omit<RetryConfig, 'onRetry'> & {
    onRetry?: (error: Error, attempt: number) => void;
  },
  operationName: string = 'retry-sync-operation'
):
  | { success: true; data: T; attempts: number }
  | { success: false; error: Error; attempts: number } {
  let lastError: Error = new Error('Unknown error');
  let attempts = 0;

  for (attempts = 1; attempts <= config.maxAttempts; attempts++) {
    try {
      const result = operation();

      if (attempts > 1) {
        logger.info(`✅ Sync retry successful: "${operationName}" after ${attempts} attempts`);
      }

      return {
        success: true,
        data: result,
        attempts,
      };
    } catch (error) {
      lastError = error as Error;

      // リトライ条件をチェック
      const shouldRetry = config.retryCondition
        ? config.retryCondition(lastError, attempts)
        : isRetryableError(lastError);

      // 最大試行回数に達した場合、またはリトライ不可能な場合
      if (attempts >= config.maxAttempts || !shouldRetry) {
        break;
      }

      logger.warn(`🔄 Sync retry ${attempts}/${config.maxAttempts}: "${operationName}" failed`);

      // onRetryコールバック実行
      if (config.onRetry) {
        config.onRetry(lastError, attempts);
      }
    }
  }

  logger.error(`❌ All sync retries failed: "${operationName}" after ${attempts} attempts`);

  return {
    success: false,
    error: lastError,
    attempts,
  };
}

/**
 * Promise配列のリトライ付き並列実行
 */
export async function retryAllAsync<T>(
  operations: Array<() => Promise<T>>,
  config: RetryConfig,
  operationName: string = 'retry-all-operations'
): Promise<Array<RetryResult<T>>> {
  const results = await Promise.all(
    operations.map((operation, index) => retryAsync(operation, config, `${operationName}-${index}`))
  );

  const successCount = results.filter(r => r.success).length;
  const failureCount = results.length - successCount;

  logger.info(`📊 Retry all completed: ${successCount} success, ${failureCount} failures`);

  return results;
}

/**
 * サーキットブレーカーパターン実装
 */
export class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';

  constructor(
    private failureThreshold: number = 5,
    private timeoutMs: number = 60000
  ) {}

  async execute<T>(
    operation: () => Promise<T>,
    operationName: string = 'circuit-breaker-operation'
  ): Promise<T> {
    // サーキットブレーカーの状態をチェック
    this.updateState();

    if (this.state === 'OPEN') {
      throw new AppError('Circuit breaker is OPEN', ErrorCategory.SYSTEM, ErrorLevel.HIGH, {
        metadata: {
          operationName,
          failureCount: this.failureCount,
          state: this.state,
        },
        retryable: false,
      });
    }

    try {
      const result = await operation();

      // 成功時のリセット
      if (this.state === 'HALF_OPEN') {
        this.reset();
        logger.info(`🔄 Circuit breaker reset to CLOSED for "${operationName}"`);
      }

      return result;
    } catch (error) {
      this.recordFailure();

      if (this.state === 'HALF_OPEN') {
        this.state = 'OPEN';
        logger.warn(`🚨 Circuit breaker opened for "${operationName}"`);
      }

      throw error;
    }
  }

  private updateState(): void {
    const now = Date.now();

    if (this.state === 'OPEN' && now - this.lastFailureTime >= this.timeoutMs) {
      this.state = 'HALF_OPEN';
      logger.info('🔄 Circuit breaker moved to HALF_OPEN');
    }
  }

  private recordFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.failureThreshold && this.state === 'CLOSED') {
      this.state = 'OPEN';
    }
  }

  private reset(): void {
    this.failureCount = 0;
    this.lastFailureTime = 0;
    this.state = 'CLOSED';
  }

  getState(): { state: string; failureCount: number; lastFailureTime: number } {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime,
    };
  }
}

/**
 * React Hook用のリトライ機能
 */
export function useRetry() {
  return {
    retryAsync,
    retrySync,
    retryAllAsync,
    isRetryableError,
    RetryConfigs,
    RetryStrategy,
    CircuitBreaker,
  };
}

/**
 * よく使用されるリトライ操作のヘルパー
 */
export const RetryHelpers = {
  /**
   * API呼び出しのリトライ
   */
  apiCall: async <T>(
    apiOperation: () => Promise<T>,
    operationName: string = 'api-call'
  ): Promise<RetryResult<T>> => {
    return retryAsync(apiOperation, RetryConfigs.API_QUICK, operationName);
  },

  /**
   * ネットワーク処理のリトライ
   */
  networkOperation: async <T>(
    networkOperation: () => Promise<T>,
    operationName: string = 'network-operation'
  ): Promise<RetryResult<T>> => {
    return retryAsync(networkOperation, RetryConfigs.NETWORK, operationName);
  },

  /**
   * 配車計算のリトライ
   */
  dispatchCalculation: async <T>(
    calculationOperation: () => Promise<T>,
    operationName: string = 'dispatch-calculation'
  ): Promise<RetryResult<T>> => {
    return retryAsync(calculationOperation, RetryConfigs.DISPATCH_CALCULATION, operationName);
  },

  /**
   * 重要な処理のリトライ
   */
  criticalOperation: async <T>(
    criticalOperation: () => Promise<T>,
    operationName: string = 'critical-operation'
  ): Promise<RetryResult<T>> => {
    return retryAsync(criticalOperation, RetryConfigs.CRITICAL, operationName);
  },
};

// =============================================================================
// FL-127で得た知見を活用したシンプルなwithRetry関数
// =============================================================================

export interface WithRetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  onRetry?: (attempt: number, error: Error) => void;
}

/**
 * シンプルなリトライラッパー関数
 * FL-127のEdgeFunctionClientで使用するパターン
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: WithRetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 30000,
    backoffFactor = 2,
    onRetry,
  } = options;

  const operationId = logger.startOperation('withRetry', { maxRetries });

  let lastError: Error;

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      logger.debug(
        'Retry attempt',
        {
          attempt,
          maxRetries: maxRetries + 1,
        },
        {
          operationId,
          context: { function: 'withRetry' },
          tags: ['retry', attempt === 1 ? 'initial' : 'retry-attempt'],
        }
      );

      const result = await operation();

      if (attempt > 1) {
        logger.info(
          'Retry successful',
          {
            attempt,
            totalAttempts: attempt,
          },
          {
            operationId,
            context: { function: 'withRetry' },
            tags: ['retry', 'success'],
          }
        );
      }

      logger.endOperation(
        operationId,
        'withRetry',
        Date.now() - parseInt(operationId.split('_')[1]),
        {
          success: true,
          attempts: attempt,
        }
      );

      return result;
    } catch (error) {
      lastError = error as Error;

      // 最後の試行の場合はエラーを投げる
      if (attempt > maxRetries) {
        logger.error(
          'All retry attempts failed',
          {
            totalAttempts: attempt - 1,
            finalError: lastError.message,
          },
          {
            operationId,
            context: { function: 'withRetry' },
            tags: ['retry', 'failed'],
          }
        );

        throw lastError;
      }

      // 指数バックオフで遅延時間を計算
      const delay = Math.min(baseDelay * Math.pow(backoffFactor, attempt - 1), maxDelay);

      logger.warn(
        'Retry attempt failed, will retry',
        {
          attempt,
          nextAttempt: attempt + 1,
          delayMs: delay,
          error: lastError.message,
        },
        {
          operationId,
          context: { function: 'withRetry' },
          tags: ['retry', 'attempt-failed'],
        }
      );

      onRetry?.(attempt, lastError);

      // 遅延実行
      if (delay > 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  // この行に到達することはないはずだが、TypeScriptのため
  throw lastError!;
}
